import { useState, useCallback } from "react";
import { gql, useMutation } from "@apollo/client";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { clientContext } from "#utils/graphql_utils";
import { isIbanCountry } from "#utils/ibanCountries";
import { type BankAccountOnboardingData, type CreatedBankAccount } from "../types";

// Hand-rolled types + inline `gql` (CREATE_BANK_ACCOUNT below) until createBankAccount
// ships in the supergraph (FF-8406 / FF-8410). No `mutations.federation.gql` document
// exists yet on purpose: codegen would fail validating an operation the schema does not
// have, and the build's GraphQL loader would choke on a stubbed file. When the mutation
// lands, move this operation into `mutations.federation.gql`, run codegen, and swap the
// gql tag + these types for the generated `useCreateBankAccountMutation`.

type CreateBankAccountMutationErrors = Array<{ field: string | null; message: string }>;

type CreateBankAccountMutationData = {
  createBankAccount: {
    bankAccount: CreatedBankAccount | null;
    errors: CreateBankAccountMutationErrors;
  };
};

type CreateBankAccountInput = {
  arenaId: string;
  country?: string;
  currency?: string;
  swiftBic?: string;
  bankNameIntl?: string;
  iban?: string;
  accountNumber?: string;
  localRouting?: BankAccountOnboardingData["localRouting"];
  ownerName?: string;
  ownerAddress?: BankAccountOnboardingData["ownerAddress"];
  notes?: string;
};

type CreateBankAccountMutationVariables = {
  input: CreateBankAccountInput;
};

const CREATE_BANK_ACCOUNT = gql`
  mutation CreateBankAccount($input: CreateBankAccountInput!) {
    createBankAccount(input: $input) {
      bankAccount {
        id
        bankInfoRedacted
        country
        currency
        swiftBic
        bankNameIntl
        paymentMethods
      }
      errors {
        field
        message
      }
    }
  }
`;

type UseCreateBankAccountOptions = {
  usCentricMode: boolean;
  onSuccess: (bankAccount: CreatedBankAccount) => void;
  onSettled?: (succeeded: boolean) => void;
};

type UseCreateBankAccountReturn = {
  createBankAccount: (state: BankAccountOnboardingData, arenaId: string) => Promise<void>;
  loading: boolean;
  mutationErrors: CreateBankAccountMutationErrors;
};

const buildInput = (
  state: BankAccountOnboardingData,
  arenaId: string,
  usCentricMode: boolean,
): CreateBankAccountInput => {
  const useIban = !usCentricMode && isIbanCountry(state.country);
  const accountField = useIban
    ? { iban: state.accountNumberOrIban }
    : { accountNumber: state.accountNumberOrIban };

  if (usCentricMode) {
    return {
      arenaId,
      ...accountField,
      swiftBic: state.swiftBic,
      bankNameIntl: state.bankNameIntl,
      ownerName: state.ownerName,
      ownerAddress: state.ownerAddress,
      notes: state.notes,
    };
  }

  return {
    arenaId,
    country: state.country,
    currency: state.currency,
    swiftBic: state.swiftBic,
    bankNameIntl: state.bankNameIntl,
    ...accountField,
    localRouting: state.localRouting,
    ownerName: state.ownerName,
    ownerAddress: state.ownerAddress,
    notes: state.notes,
  };
};

export const useCreateBankAccount = ({
  usCentricMode,
  onSuccess,
  onSettled,
}: UseCreateBankAccountOptions): UseCreateBankAccountReturn => {
  const [mutationErrors, setMutationErrors] = useState<CreateBankAccountMutationErrors>([]);

  const [mutate, { loading, error }] = useMutation<
    CreateBankAccountMutationData,
    CreateBankAccountMutationVariables
  >(CREATE_BANK_ACCOUNT, { context: clientContext() });

  useReportError(error ? formatError(error) : null);

  const createBankAccount = useCallback(
    async (state: BankAccountOnboardingData, arenaId: string): Promise<void> => {
      setMutationErrors([]);
      try {
        const result = await mutate({
          variables: { input: buildInput(state, arenaId, usCentricMode) },
        });

        const errors = result.data?.createBankAccount?.errors ?? [];
        const bankAccount = result.data?.createBankAccount?.bankAccount ?? null;

        setMutationErrors(errors);

        if (errors.length === 0 && bankAccount !== null) {
          onSuccess(bankAccount);
          onSettled?.(true);
        } else {
          onSettled?.(false);
        }
      } catch {
        // Apollo network / transport errors are already reported via
        // useReportError above. Surface a generic message so the modal shows
        // inline feedback and the user knows the submission failed.
        setMutationErrors([
          { field: null, message: "Something went wrong. Please try again." },
        ]);
        onSettled?.(false);
      }
    },
    [mutate, usCentricMode, onSuccess, onSettled],
  );

  return { createBankAccount, loading, mutationErrors };
};
